import { gql } from '@apollo/client';

import { Exact, Forms, Scalars, SearchInput } from '@/graphql/contracts/_generated_/contracts';

export const SearchSummaryFragmentDoc = gql`
  fragment SearchSummary on Search {
    rowKey
    form
    title
    description
    isPrivate
    value @client
    label @client
  }
`;

export const GetSavedSearchesDocument = gql`
  query GetSavedSearches($form: Forms!) {
    config {
      savedSearches(filter: { form: $form }) {
        ...SearchSummary
      }
    }
  }
  ${SearchSummaryFragmentDoc}
`;

export const SearchFragmentDoc = gql`
  fragment Search on Search {
    rowKey
    form
    title
    description
    isPrivate
    isOwner
    filter
  }
`;

export const GetSavedSearchDocument = gql`
  query GetSavedSearch($form: Forms!, $rowKey: String!) {
    config {
      savedSearches(filter: { form: $form, rowKey: $rowKey }) {
        ...Search
      }
    }
  }
  ${SearchFragmentDoc}
`;

export const ValidationItemDetailsFragmentDoc = gql`
  fragment ValidationItemDetails on ValidationItem {
    message
    details {
      primaryKey
      field
      value
    }
  }
`;

export const FailedValidationDetailsFragmentDoc = gql`
  fragment FailedValidationDetails on FailedValidation {
    errors {
      ...ValidationItemDetails
    }
    warnings {
      ...ValidationItemDetails
    }
  }
  ${ValidationItemDetailsFragmentDoc}
`;

export const SucceededDetailsFragmentDoc = gql`
  fragment SucceededDetails on Succeeded {
    succeeded
  }
`;

export const UnexpectedErrorDetailsFragmentDoc = gql`
  fragment UnexpectedErrorDetails on UnexpectedError {
    errorMessage
  }
`;

export const SaveSearchDocument = gql`
  mutation SaveSearch($search: SearchInput!) {
    config {
      savedSearch {
        saveSearch(search: $search) {
          ... on SearchRowKey {
            rowKey
          }
          ... on FailedValidation {
            ...FailedValidationDetails
          }
          ... on UnexpectedError {
            ...UnexpectedErrorDetails
          }
        }
      }
    }
  }
  ${FailedValidationDetailsFragmentDoc}
  ${UnexpectedErrorDetailsFragmentDoc}
`;

export const UpdateSavedSearchDocument = gql`
  mutation UpdateSavedSearch($search: SearchInput!) {
    config {
      savedSearch {
        updateSearch(search: $search) {
          ... on SearchRowKey {
            rowKey
          }
          ... on FailedValidation {
            ...FailedValidationDetails
          }
          ... on UnexpectedError {
            ...UnexpectedErrorDetails
          }
        }
      }
    }
  }
  ${FailedValidationDetailsFragmentDoc}
  ${UnexpectedErrorDetailsFragmentDoc}
`;

export const DeleteSavedSearchDocument = gql`
  mutation DeleteSavedSearch($form: Forms!, $rowKey: String!) {
    config {
      savedSearch {
        deleteSearch(form: $form, rowKey: $rowKey) {
          ... on Succeeded {
            ...SucceededDetails
          }
          ... on FailedValidation {
            ...FailedValidationDetails
          }
          ... on UnexpectedError {
            ...UnexpectedErrorDetails
          }
        }
      }
    }
  }
  ${SucceededDetailsFragmentDoc}
  ${FailedValidationDetailsFragmentDoc}
  ${UnexpectedErrorDetailsFragmentDoc}
`;

export type GetSavedSearchesQuery = {
  __typename?: 'Query';
  config?: {
    __typename?: 'ConfigQueries';
    savedSearches?: Array<{
      __typename?: 'Search';
      rowKey: string;
      form?: Forms | null;
      title?: string | null;
      description?: string | null;
      isPrivate: boolean;
      value?: string | null;
      label?: string | null;
    }> | null;
  } | null;
};

export type GetSavedSearchesQueryVariables = Exact<{
  form: Forms;
}>;

export type GetSavedSearchQuery = {
  __typename?: 'Query';
  config?: {
    __typename?: 'ConfigQueries';
    savedSearches?: Array<{
      __typename?: 'Search';
      rowKey: string;
      form?: Forms | null;
      title?: string | null;
      description?: string | null;
      isPrivate: boolean;
      isOwner?: boolean | null;
      filter?: string | null;
    }> | null;
  } | null;
};

export type GetSavedSearchQueryVariables = Exact<{
  form: Forms;
  rowKey: Scalars['String']['input'];
}>;

export type SaveSearchMutation = {
  __typename?: 'Mutation';
  config?: {
    __typename?: 'ConfigMutations';
    savedSearch?: {
      __typename?: 'SavedSearchMutation';
      saveSearch?:
      | {
        __typename?: 'FailedValidation';
        errors: Array<{
          __typename?: 'ValidationItem';
          message: string;
          details: Array<{
            __typename?: 'ValidationItemDetails';
            primaryKey?: string | null;
            field?: string | null;
            value?: string | null;
          }>;
        }>;
        warnings: Array<{
          __typename?: 'ValidationItem';
          message: string;
          details: Array<{
            __typename?: 'ValidationItemDetails';
            primaryKey?: string | null;
            field?: string | null;
            value?: string | null;
          }>;
        }>;
      }
      | { __typename?: 'SearchRowKey'; rowKey: string }
      | { __typename?: 'UnexpectedError'; errorMessage: string }
      | null;
    } | null;
  } | null;
};

export type SaveSearchMutationVariables = Exact<{
  search: SearchInput;
}>;


export type UpdateSavedSearchMutation = {
  __typename?: 'Mutation';
  config?: {
    __typename?: 'ConfigMutations';
    savedSearch?: {
      __typename?: 'SavedSearchMutation';
      updateSearch?:
      | {
        __typename?: 'FailedValidation';
        errors: Array<{
          __typename?: 'ValidationItem';
          message: string;
          details: Array<{
            __typename?: 'ValidationItemDetails';
            primaryKey?: string | null;
            field?: string | null;
            value?: string | null;
          }>;
        }>;
        warnings: Array<{
          __typename?: 'ValidationItem';
          message: string;
          details: Array<{
            __typename?: 'ValidationItemDetails';
            primaryKey?: string | null;
            field?: string | null;
            value?: string | null;
          }>;
        }>;
      }
      | { __typename?: 'SearchRowKey'; rowKey: string }
      | { __typename?: 'UnexpectedError'; errorMessage: string }
      | null;
    } | null;
  } | null;
};

export type UpdateSavedSearchMutationVariables = Exact<{
  search: SearchInput;
}>;

export type DeleteSavedSearchMutation = {
  __typename?: 'Mutation';
  config?: {
    __typename?: 'ConfigMutations';
    savedSearch?: {
      __typename?: 'SavedSearchMutation';
      deleteSearch?:
      | {
        __typename?: 'FailedValidation';
        errors: Array<{
          __typename?: 'ValidationItem';
          message: string;
          details: Array<{
            __typename?: 'ValidationItemDetails';
            primaryKey?: string | null;
            field?: string | null;
            value?: string | null;
          }>;
        }>;
        warnings: Array<{
          __typename?: 'ValidationItem';
          message: string;
          details: Array<{
            __typename?: 'ValidationItemDetails';
            primaryKey?: string | null;
            field?: string | null;
            value?: string | null;
          }>;
        }>;
      }
      | { __typename?: 'Succeeded'; succeeded: boolean }
      | { __typename?: 'UnexpectedError'; errorMessage: string }
      | null;
    } | null;
  } | null;
};

export type DeleteSavedSearchMutationVariables = Exact<{
  form: Forms;
  rowKey: Scalars['String']['input'];
}>;